import useSubScriptionMutation from "@/api/mutation/useSubScriptionMutation";
import { Button } from "../ui/button";
import crossNew from "@/assets/svg/CrossIcon.svg";
import {
  BillingCycleSchema,
  ExtendPlanTypeEnumSchema,
  PlanTypeEnumSchema,
} from "@backend/src/schemas/enums";
import { useUser } from "@/hooks/useUser";
import { toast } from "react-toastify";
import { errorConfig } from "@/lib/toastError";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useGetInvoicePortalQuery from "@/api/query/useInvoiceProtalQuary";
import useUpgradeSubScriptionMutation from "@/api/mutation/useUpgradeSubScriptionMutation";
import Dialog from "../common/Dialog";
import Warning from "@/assets/svg/Warning.svg";
import Loader from "../common/Loader";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import GreenChackMark from "@/assets/svg/GreenChackMark.svg";
import RedCharmTick from "@/assets/svg/RedCharmTick.svg";
import Spinner from "../ui/spinner";
import dateFormater from "@/helperFuntions/dateFormater";
import useRibbonsListQuery from "@/api/query/useRibbonMessageQuery";
import useProjectQuery from "@/api/query/useProjectQuery";
import { FourteenDayCounterStartNow } from "../common/DaysCounter";
import { Check } from "lucide-react";

export interface DowngradeDetails {
  currentPlan: CurrentPlan;
  downgradePlan: DowngradePlan;
}

export interface CurrentPlan {
  planType: string;
  billingCycle: string;
}

export interface DowngradePlan {
  planType: string;
  billingCycle: string;
}

const PaymentPlans = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { mutate: checkout, isPending } = useSubScriptionMutation();
  const { mutate: upgradeToEnterprise } = useUpgradeSubScriptionMutation();
  const { user } = useUser();
  const [isAnnual, setIsAnnual] = useState(false);
  const navigate = useNavigate();
  const [isPaymentSuccess, setIsPaymentSuccess] = useState<{
    loading: boolean;
    status: "PENDING" | "SUCCESS" | "ERROR";
  }>({
    loading: false,
    status: "PENDING",
  });
  const userQuary = useCurrentUserQuery();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loader, setLoader] = useState(false);
  const ribbonsListQuery = useRibbonsListQuery();
  const [discount, setDiscount] = useState<{
    discount: number;
    months: number;
    couponCode: string;
  }>();
  const [coupon] = useSearchParams();
  const [selectedPlan, setSelectedPlan] =
    useState<keyof typeof PlanTypeEnumSchema>();
  const [downgrade, setDowngrade] = useState<keyof typeof PlanTypeEnumSchema>();

  const handleCheckout = (
    planType: keyof typeof PlanTypeEnumSchema,
    isUpgrade: boolean = false
  ) => {
    if (!user?.email) {
      toast.error("Please login to continue");
      return;
    }
    if (text(planType) == t("DowngradeToThisPlan") && !downgrade) {
      setDowngrade(planType);
      return;
    }
    if (
      user.subscription &&
      user.subscription.planType &&
      text(planType) == t("UpgradeToThisPlan") &&
      (user.subscription.customerId !== ExtendPlanTypeEnumSchema.FREE_TRIAL ) 
    ) {
      if (isUpgrade) {
        setIsPaymentSuccess({ loading: true, status: "PENDING" });
      }
      upgradeToEnterprise(
        {
          email: user?.email,
          planType: errorMessage && selectedPlan ? selectedPlan : planType,
          billingCycle: isAnnual
            ? BillingCycleSchema.YEAR
            : BillingCycleSchema.MONTH,
          isUpgrade: isUpgrade,
        },
        {
          onSuccess(data) {
            setLoader(false);
            setErrorMessage("");
            toast.success(data.data.message, errorConfig);
            userQuary.refetch();
            setIsPaymentSuccess({ loading: true, status: "SUCCESS" });
          },
          onError(error) {
            if (isUpgrade) {
              setIsPaymentSuccess({ loading: true, status: "ERROR" });
            }
            setLoader(false);
            if (
              error.response?.data.message.includes(
                "You card will be charged only"
              )
            ) {
              setErrorMessage(error.response.data.message);
              setSelectedPlan(planType);
            } else {
              toast.error(error.response?.data.message, errorConfig);
            }
          },
        }
      );
    } else {
      setLoader(true);
      setDowngrade(undefined);
      const couponCode = coupon.get("coupon") ?? "";
      checkout(
        {
          email: user?.email,
          planType: planType,
          billingCycle: isAnnual
            ? BillingCycleSchema.YEAR
            : BillingCycleSchema.MONTH,
          couponCode: couponCode,
        },
        {
          onSuccess(data) {
            navigate(`/checkout/${data.data.data.client_secret}`);
            onClose();
            setLoader(false);
          },
          onError(error) {
            if (
              error.response?.data.message.includes(
                "Your plan will be downgraded"
              )
            ) {
              toast.error(
                t(
                  "Your plan will be downgraded, but is still available until the end of your billing period on"
                ) +
                  ` (${
                    error.response?.data.message.split("billing period on")[1]
                  })`,
                errorConfig
              );
            } else {
              toast.error(error.response?.data.message, errorConfig);
            }
            setLoader(false);
            userQuary.refetch();
          },
        }
      );
    }
  };

  const { refetch, isRefetching } = useGetInvoicePortalQuery();
  const invoicePortal = () => {
    refetch()
      .then((res) => {
        if (res.data) {
          window.open(res.data.data.data, "_blank");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data.message, errorConfig);
      });
  };
  const allow = (key: keyof typeof PlanTypeEnumSchema) => {
    if(user?.subscription?.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL) return false;
    if (user) {
      return (
        user.subscription?.planType === key &&
        user.subscription?.customerId &&
        user.subscription?.subScriptionId &&
        user.subscription?.billingCycle ==
          (isAnnual ? BillingCycleSchema.YEAR : BillingCycleSchema.MONTH)
      );
    }
    return false;
  };

  const text = (key: keyof typeof PlanTypeEnumSchema) => {
    if (user && user.subscription) {
      if(user.subscription.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL) {
        return t("UpgradeToThisPlan");
      }
      if (
        user.subscription?.billingCycle === BillingCycleSchema.MONTH &&
        isAnnual
      ) {
        return t("UpgradeToThisPlan");
      } else if (
        user?.subscription?.billingCycle === BillingCycleSchema.YEAR &&
        !isAnnual
      ) {
        return t("DowngradeToThisPlan");
      } else {
        if (
          key == PlanTypeEnumSchema.BUSINESS &&
          user.subscription?.planType == PlanTypeEnumSchema.ENTERPRISE
        ) {
          return t("DowngradeToThisPlan");
        }
        if (
          key == PlanTypeEnumSchema.ENTERPRISE &&
          user.subscription?.planType == PlanTypeEnumSchema.BUSINESS
        ) {
          return t("UpgradeToThisPlan");
        }
      }
    }
    return t("UpgradeToThisPlan");
  };
  const location = window.location.pathname;
  const projectQuery = useProjectQuery();

  useEffect(() => {
    if (ribbonsListQuery.data?.data.data[0]) {
      //   const ribbon = ribbonsListQuery.data?.data.data[0];
      //   const currantTime = new Date();
      //   const ribbonStartDate = new Date(ribbon.startDate);
      //   const ribbonExpiry = new Date(ribbon.validUntil);
      //   currantTime.setHours(0, 0, 0, 0);
      //   ribbonStartDate.setHours(0, 0, 0, 0);
      //   ribbonExpiry.setHours(0, 0, 0, 0);

      //   const isTodayStart = currantTime.getTime() === ribbonStartDate.getTime();
      //   const isTodayExpiry = currantTime.getTime() === ribbonExpiry.getTime();
      //   const isWithinRange = currantTime >= ribbonStartDate && currantTime <= ribbonExpiry;
      //   if (isTodayStart || isTodayExpiry || isWithinRange) {
      //     setDiscount({
      //       discount: ribbon.discountPercentage,
      //       months: ribbon.couponDuration,
      //       couponCode: ribbon.couponCode,
      //     });
      //   }
      //   if (user && user.subscription?.planType == PlanTypeEnumSchema.ENTERPRISE) {
      //     setDiscount(undefined)
      //   }
      if (coupon.get("coupon")) {
        const ribbon = ribbonsListQuery.data?.data.data[0];

        setDiscount({
          discount: ribbon.discountPercentage,
          months: ribbon.couponDuration,
          couponCode: ribbon.couponCode,
        });
      }
    }
  }, [ribbonsListQuery.data?.data.data, coupon]);

  useEffect(() => {
    if (user?.subscription?.billingCycle) {
      setIsAnnual(user?.subscription?.billingCycle == BillingCycleSchema.YEAR);
    }
  }, [user]);

  const cancel = useCallback(
    (key: keyof typeof PlanTypeEnumSchema) => {
      if (user?.subscription?.downgradeDetails && user?.subscription?.endDate) {
        const downgradeDetails = JSON.parse(
          user.subscription.downgradeDetails
        ) as DowngradeDetails;
        return (
          downgradeDetails.downgradePlan.billingCycle ===
            (!isAnnual ? BillingCycleSchema.MONTH : BillingCycleSchema.YEAR) &&
          downgradeDetails.downgradePlan.planType === key
        );
      }

      if (
        key === PlanTypeEnumSchema.BUSINESS &&
        !isAnnual &&
        user?.subscription?.endDate
      ) {
        return true;
      }

      return false;
    },
    [isAnnual, user]
  );

  function getDiscountPercentage(
    discountPercentage: number,
    actualValue: number
  ): string {
    const discountValue = (discountPercentage / 100) * actualValue;
    const finalPrice = actualValue - discountValue;
    return t("subscription.offer", {
      count: isAnnual ? 1 : discount?.months,
      period: isAnnual ? t("subscription.year") : t("subscription.months"),
      price: finalPrice.toFixed(2),
    });
  }

  return (
    <div className="overflow-auto h-full px-6">
      <div className="flex flex-col flex-1 w-full max-w-6xl mx-auto">
        {loader && (
          <Loader className="absolute h-full w-full top-0 left-0 z-[99]" />
        )}
        <div className="flex justify-between items-center w-full mb-2">
          {location !== "/plans" && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Close"
            >
              <img src={crossNew} alt="" className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="w-full flex flex-col items-center justify-center mb-7">
          <h2 className="text-2xl font-sans text-gray-900 mb-4 text-center w-full">
            {t("UpgradePlan")}
          </h2>
          <div className="inline-flex p-1 bg-gray-200 border border-gray-300 border-opacity-30 rounded-full">
            <button
              type="button"
              onClick={() => setIsAnnual(false)}
              className={`min-w-[130px] py-2.5 px-6 rounded-full text-sm font-semibold transition-colors ${
                !isAnnual
                  ? "bg-orange text-white shadow-sm"
                  : "text-gray-500 bg-transparent hover:text-gray-700"
              }`}
            >
              {t("Monthly")}
            </button>
            <button
              type="button"
              onClick={() => setIsAnnual(true)}
              className={`min-w-[130px] py-2.5 px-6 rounded-full text-sm font-semibold transition-colors ${
                isAnnual
                  ? "bg-orange text-white shadow-sm"
                  : "text-gray-500 bg-transparent hover:text-gray-700"
              }`}
            >
              {t("Annual")}
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2 text-center">
            {t("2MonthsFree")} & 20% OFF on {t("Annual")} plan
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 w-full justify-center items-stretch max-w-[1100px] mx-auto">
          {user?.subscription?.customerId === "FREE_TRIAL" ? (
            <div className="bg-white relative flex flex-col rounded-2xl p-6 w-72 max-w-[343px] text-gray-900 border-2 border-gray-200 shadow-sm flex-1 min-h-[480px]">
              {user.subscription.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 bg-plan-currentPlan text-white text-[10px] font-medium text-sm text-center content-center rounded-b-xl shadow-sm z-10 whitespace-nowrap w-32 h-6">
                  {t("CurrentPlan")}
                </span>
              )}
              <div className="text-left mt-16 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{t("Free_Trial")}</h2>
                {user?.subscription?.customerId === "FREE_TRIAL" && (
                  <FourteenDayCounterStartNow startDate={user.subscription.createdAt} />
                )}
                <p className="text-sm mt-4 text-gray-900"><span className="text-4xl font-semibold">€0 </span>{isAnnual ? t("perYear") : t("perMonth")}</p>
                <hr className="border-gray-200 mt-4" />
              </div>
              <ul className="space-y-2.5 text-sm text-gray-600 mb-6 flex-1">
                {[t("UnlimitedProjects"), t("UnlimitedProjectMembers"), t("Resources workload"), t("RiskManagement"), t("ProjectDashboard"), t("portfolio_dashboard"), t("Timesheet"), t("App Client"), t("OnboardingAndTraining")].map((label) => (
                  <li key={label} className="flex items-center gap-2">
                    <Check className="w-4 h-4 shrink-0 text-gray-400" strokeWidth={2.5} />
                    {label}
                  </li>
                ))}
              </ul>
              <div className="mt-auto flex justify-center">
              {user?.subscription?.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL ? (
                <div className="w-full py-3 px-4 rounded-lg bg-gray-200 text-gray-600 text-center font-semibold text-sm">
                  {t("CurrentPlan")}
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg"
                  onClick={() => handleCheckout(PlanTypeEnumSchema.FREE)}
                >
                  {t("TryForFree")}
                </Button>
              )}
              </div>
            </div>
          ) : (
            <div className={`bg-white relative flex flex-col rounded-2xl p-6 w-72 max-w-80 text-gray-900 border-2 shadow-sm flex-1 min-h-[480px] ${!user?.subscription?.planType || allow(PlanTypeEnumSchema.FREE) ? "border-primary" : "border-gray-200"}`}>
              {(!user?.subscription || user?.subscription?.planType === PlanTypeEnumSchema.FREE) && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-plan-currentPlan text-white text-[10px] font-semibold px-3 py-1.5 rounded shadow-sm z-10 whitespace-nowrap">
                  {t("CurrentPlan")}
                </span>
              )}
              <div className="text-left mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{t("Free")}</h2>
                <p className="text-sm mt-4 text-gray-900"><span className="text-4xl font-semibold">€0 </span>{isAnnual ? t("perYear") : t("perMonth")}</p>
                <hr className="border-gray-200 mt-4" />
              </div>
              <ul className="space-y-2.5 text-sm text-gray-600 mb-6 flex-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0 text-gray-400" strokeWidth={2.5} /> 2 {t("projects")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0 text-gray-400" strokeWidth={2.5} /> 1 {t("ProjectManager")}</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0 text-gray-400" strokeWidth={2.5} /> 2 {t("ProjectMembers")}</li>
              </ul>
              <div className="mt-auto" />
            </div>
          )}
          <div className={`relative bg-white flex flex-col rounded-2xl w-72 max-w-80 text-gray-900 border-2 flex-1 overflow-hidden border-plan-business min-h-[480px] ${allow(PlanTypeEnumSchema.BUSINESS) ? "!border-plan-business shadow-md" : ""}`}>
            <div className="bg-plan-businessLight text-plan-business text-center text-sm font-semibold py-2.5 px-4 mt-8">
              {t("IdealForFreelancersAndProjectManagers")}
            </div>
            <div className="p-6 pt-4 flex flex-col flex-1">
            {allow(PlanTypeEnumSchema.BUSINESS) && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 bg-plan-currentPlan text-white text-[10px] font-medium text-sm text-center content-center rounded-b-xl shadow-sm z-10 whitespace-nowrap w-32 h-6">
                {t("CurrentPlan")}
              </span>
            )}
            <h2 className="text-lg font-semibold text-gray-900 text-left">{t("Business")}</h2>
            <p className="text-4xl font-semibold text-gray-900 mt-4">
              <span className={discount ? "line-through text-gray-500 text-4xl font-semibold" : ""}>{isAnnual ? "€390" : "€39"}</span>
              <span className="text-sm font-normal text-gray-500 ml-1">{isAnnual ? t("perYear") : t("perMonth")}</span>
            </p>
            {discount && (
              <p className="text-xs text-plan-business mb-2">
                {isAnnual ? getDiscountPercentage(discount.discount, 390) : getDiscountPercentage(discount.discount, 39)}
              </p>
            )}
            <hr className="border-gray-200 mt-4 mb-4" />
            <ul className="space-y-2.5 text-sm text-gray-600 mb-6 flex-1">
              {["5 " + t("projects"), "1 " + t("ProjectManager"), "5 " + t("ProjectMembers"), t("Resources workload"), t("RiskManagement"), t("ProjectDashboard"), t("portfolio_dashboard"), t("OnboardingAndTraining")].map((label) => (
                <li key={label} className="flex items-center gap-2">
                  <Check className="w-4 h-4 shrink-0 text-plan-business" strokeWidth={2.5} />
                  <span className="text-gray-900">{label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto flex justify-center">
            <Button
              disabled={(isPending && isRefetching) || cancel(PlanTypeEnumSchema.BUSINESS)}
              isLoading={isPending || isRefetching}
              className="w-full bg-[#3BB8FF] hover:bg-[#3BB8FF]/90 text-white font-semibold rounded-lg"
              onClick={() => (allow(PlanTypeEnumSchema.BUSINESS) ? invoicePortal() : handleCheckout(PlanTypeEnumSchema.BUSINESS))}
            >
              {allow(PlanTypeEnumSchema.BUSINESS) ? t("ViewInvoice") : text(PlanTypeEnumSchema.BUSINESS)}
            </Button>
            </div>
            {cancel(PlanTypeEnumSchema.BUSINESS) && user?.subscription?.endDate && (
              <span className="text-xs text-gray-500 text-center mt-2">
                {t("Your plan will be downgraded, but is still available until the end of your billing period on")} ({dateFormater(user.subscription.endDate)})
              </span>
            )}
            </div>
          </div>
          <div className={`relative bg-white flex flex-col rounded-2xl w-72 max-w-80 text-gray-900 border-2 flex-1 overflow-hidden border-plan-enterprise min-h-[480px] ${allow(PlanTypeEnumSchema.ENTERPRISE) ? "!border-plan-enterprise shadow-md" : ""}`}>
            <div className="bg-plan-enterpriseLight text-plan-enterprise text-center text-sm font-semibold py-2.5 px-4 mt-8">
              {t("IdealForMidSizeAndLargeTeams")}
            </div>
            <div className="p-6 pt-4 flex flex-col flex-1">
            {allow(PlanTypeEnumSchema.ENTERPRISE) && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 bg-plan-currentPlan text-white text-[10px] font-medium text-sm text-center content-center rounded-b-xl shadow-sm z-10 whitespace-nowrap w-32 h-6">
                {t("CurrentPlan")}
              </span>
            )}
            <h2 className="text-lg font-semibold text-gray-900 text-left">{t("Enterprise")}</h2>
            <p className="text-4xl font-semibold text-gray-900 mt-4">
              <span className={discount ? "line-through text-gray-500" : ""}>{isAnnual ? "€690" : "€69"}</span>
              <span className="text-sm font-normal text-gray-500 ml-1">{isAnnual ? t("perYear") : t("perMonth")}</span>
            </p>
            <p className={`text-xs text-gray-500 ${discount ? "" : "mb-3"}`}>+{isAnnual ? "490" : "49"}€ {isAnnual ? t("perYear") : t("perMonth")} {t("perProjectManager")}</p>
            {discount && (
              <p className="text-xs text-plan-enterprise mb-3">
                {isAnnual ? getDiscountPercentage(discount.discount, 690) : getDiscountPercentage(discount.discount, 69)}
              </p>
            )}
            <hr className="border-gray-200 mt-0 mb-4" />
            <ul className="space-y-2.5 text-sm text-gray-600 mb-6 flex-1">
                {[t("UnlimitedProjects"), t("UnlimitedProjectMembers"), t("Resources workload"), t("RiskManagement"), t("ProjectDashboard"), t("portfolio_dashboard"), t("Timesheet"), t("App Client"), t("OnboardingAndTraining")].map((label) => (
                <li key={label} className="flex items-center gap-2">
                  <Check className="w-4 h-4 shrink-0 text-plan-enterprise" strokeWidth={2.5} />
                  <span className="text-gray-900">{label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto flex justify-center">
            <Button
              disabled={(isPending && isRefetching) || cancel(PlanTypeEnumSchema.ENTERPRISE)}
              isLoading={isPending || isRefetching}
              className="w-full bg-[#C056F9] hover:bg-[#C056F9]/90 text-white font-semibold rounded-lg"
              onClick={() => (allow(PlanTypeEnumSchema.ENTERPRISE) ? invoicePortal() : handleCheckout(PlanTypeEnumSchema.ENTERPRISE))}
            >
              {allow(PlanTypeEnumSchema.ENTERPRISE) ? t("ViewInvoice") : text(PlanTypeEnumSchema.ENTERPRISE)}
            </Button>
            </div>
            {cancel(PlanTypeEnumSchema.ENTERPRISE) && user?.subscription?.endDate && (
              <span className="text-xs text-gray-500 text-center mt-2">
                {t("Your plan will be downgraded, but is still available until the end of your billing period on")} ({dateFormater(user.subscription.endDate)})
              </span>
            )}
            </div>
          </div>
        </div>
        <Dialog
          isOpen={Boolean(errorMessage)}
          onClose={() => {}}
          modalClass="rounded-lg w-3/11"
        >
          <div className="flex flex-col gap-2 p-6 ">
            <img src={Warning} className="w-12 m-auto opacity-70" />{" "}
            {t(
              "You card will be charged only {{price}}€ for this {{duration}}. Please confirm this upgrade.",
              {
                price: errorMessage
                  ? errorMessage.split("{{")[1].split("}}")[0]
                  : "",
                duration: t(isAnnual ? "year" : "month"),
              }
            )}
            <div className="flex gap-2 ml-auto mt-4">
              <Button variant={"outline"} onClick={() => setErrorMessage("")}>
                {t("cancel")}
              </Button>
              <Button
                variant={"primary"}
                className={"text-white"}
                onClick={() => {
                  handleCheckout(PlanTypeEnumSchema.ENTERPRISE, true);
                }}
              >
                {t("confirm")}
              </Button>
            </div>
          </div>
        </Dialog>
        <Dialog
          isOpen={Boolean(downgrade)}
          onClose={() => {}}
          modalClass="rounded-lg w-3/11"
        >
          <div className="flex flex-col gap-2 p-6 ">
            <img src={Warning} className="w-12 m-auto opacity-70" />{" "}
            {t(
              "You'll be downgraded in the next billing cycle. Are you sure you want to downgrade?"
            )}
            <div className="flex gap-2 ml-auto mt-4">
              <Button
                variant={"outline"}
                onClick={() => setDowngrade(undefined)}
              >
                {t("cancel")}
              </Button>
              <Button
                variant={"primary"}
                className={"text-white"}
                onClick={() => {
                  downgrade ? handleCheckout(downgrade) : "";
                }}
              >
                {t("confirm")}
              </Button>
            </div>
          </div>
        </Dialog>
        <Dialog
          isOpen={isPaymentSuccess.loading}
          onClose={() => {}}
          modalClass="rounded-lg "
        >
          <div className=" w-80 h-fit flex flex-col gap-6 p-8 relative">
            {isPaymentSuccess.status == "ERROR" && (
              <div
                className="flex justify-end cursor-pointer items-center w-full h-1/4"
                onClick={() => {
                  setIsPaymentSuccess({ loading: false, status: "PENDING" });
                }}
              >
                <img src={crossNew} className="w-4 max-w-4" />
              </div>
            )}
            <div className="flex justify-center  w-full h-3/4   items-center">
              {isPaymentSuccess.status === "SUCCESS" ? (
                <div className="flex justify-center items-center animate-fadeInScale rounded-full w-fit h-fit p-4 bg-green-500/50">
                  <img src={GreenChackMark} className="w-20 h-20" />
                </div>
              ) : isPaymentSuccess.status == "PENDING" ? (
                <Spinner color="#535EF9" className="h-20 w-20" />
              ) : (
                <div className="flex justify-center items-center animate-fadeInScale rounded-full w-fit h-fit p-4 bg-red-500/50">
                  <img src={RedCharmTick} className="w-20 h-20" />
                </div>
              )}
            </div>
            <div className="text-center text-xl font-semibold">
              {isPaymentSuccess.status === "SUCCESS"
                ? "Payment Success"
                : isPaymentSuccess.status == "PENDING"
                ? "Payment is in progress"
                : "Payment Failed"}
              <div className="text-center text-sm text-gray-450">
                {isPaymentSuccess.status === "SUCCESS" ? (
                  <div className="flex flex-col gap-2">
                    <p>{t("Your subscription is active now")}</p>
                    <Button
                      onClick={() => {
                        setIsPaymentSuccess({
                          loading: false,
                          status: "PENDING",
                        });
                        projectQuery.refetch();
                      }}
                      variant={"primary"}
                    >
                      Ok
                    </Button>
                  </div>
                ) : isPaymentSuccess.status == "PENDING" ? (
                  "Please wait while we process your payment"
                ) : (
                  <div className="flex flex-col gap-2">
                    <p> Please try again</p>
                    <Button
                      onClick={() => {
                        setIsPaymentSuccess({
                          loading: false,
                          status: "PENDING",
                        });
                        handleCheckout(PlanTypeEnumSchema.ENTERPRISE, true);
                      }}
                      variant={"primary"}
                    >
                      Try again
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default PaymentPlans;
